import { extraErrorDataIntegration, init } from '@sentry/react';

import { beforeBreadcrumb } from './beforeBreadcrumb';
import type { BeforeSendHooks } from './beforeSend';
import { createBeforeSend } from './beforeSend';
import { resolveSentryEnvironment } from './environment';

/** Derived from an integration factory rather than imported — the SDK does not export the type. */
type SentryIntegration = ReturnType<typeof extraErrorDataIntegration>;

export type InitSentryOptions = {
  dsn: string;
  /** The build's `$TURBO_HASH`; required but nullable so an app states it explicitly. */
  release: string | undefined;
  /** A dev build reports nothing; the host check can't tell, as Vite serves on `0.0.0.0`. */
  isDevBuild: boolean;
  /** Extends the shared noise filter; it cannot be replaced, so shared rules always apply. */
  hooks?: BeforeSendHooks;
  /** Production hosts the shared policy would reject, e.g. a `.vercel.app` production alias. */
  productionHosts?: readonly string[];
  /** Appended to `extraErrorDataIntegration()`. */
  integrations?: SentryIntegration[];
};

/**
 * Starts Sentry error reporting for an app.
 *
 * - Reports only from a non-dev build on a host the shared policy accepts.
 * - Errors only: no tracing, no session replay.
 * - `'report-only'` keeps the SDK's appended API hostname out of the user-facing `error.message`.
 * - `beforeSend` and `beforeBreadcrumb` are fixed, so an app cannot drop the shared rules.
 */
export const initSentry = ({
  dsn,
  release,
  isDevBuild,
  hooks,
  productionHosts,
  integrations = [],
}: InitSentryOptions): void => {
  if (isDevBuild) return;

  const environment = resolveSentryEnvironment(window.location.hostname, productionHosts);
  if (!environment) return;

  init({
    dsn,
    environment,
    release,
    enhanceFetchErrorMessages: 'report-only',
    integrations: [extraErrorDataIntegration(), ...integrations],
    beforeSend: createBeforeSend(hooks),
    beforeBreadcrumb,
  });
};
